// JavaScript port of key PHP Util methods used throughout the filter and insight engines.
// Mirrors api/fuel/app/classes/util.php

import DB from './db'

var Util = {

	// Port of Util::query($sql, $cacher_key, $index, $grouped)
	// Runs SQL against local SQLite and returns array of row objects.
	// $cacher_key is ignored locally. $index keys results by that field.
	// $grouped groups multiple rows under the same key.
	query: function(sql, cacherKey, index, grouped) {
		var results = DB.query(sql)

		if (index) {
			var keyed = {}
			for (var i = 0; i < results.length; i++) {
				var k = results[i][index]
				if (grouped) {
					if (!keyed[k]) keyed[k] = []
					keyed[k].push(results[i])
				} else {
					keyed[k] = results[i]
				}
			}
			return keyed
		}

		return results
	},

	// Hot-loop fast variant of Util.query for queries that return thousands
	// of rows. Uses DB.queryFast() which avoids per-row stmt.getAsObject()
	// overhead. Drop-in replacement for Util.query — supports the same
	// `index`/`grouped` post-processing arguments.
	queryFast: function(sql, cacherKey, index, grouped) {
		var results = DB.queryFast(sql)

		if (index) {
			var keyed = {}
			for (var i = 0; i < results.length; i++) {
				var k = results[i][index]
				if (grouped) {
					if (!keyed[k]) keyed[k] = []
					keyed[k].push(results[i])
				} else {
					keyed[k] = results[i]
				}
			}
			return keyed
		}

		return results
	},

	// Port of Util::pluck($array, 'field')
	// Extract a single field from each row.
	pluck: function(array, field) {
		var result = []
		for (var i = 0; i < array.length; i++) {
			if (array[i][field] !== undefined) result.push(array[i][field])
		}
		return result
	},

	// Port of Util::associated($array, 'key', 'value')
	// Rows -> key=>value map. If value is omitted, maps key=>row.
	associated: function(array, key, value) {
		var result = {}
		for (var i = 0; i < array.length; i++) {
			if (value !== undefined) {
				result[array[i][key]] = array[i][value]
			} else {
				result[array[i][key]] = array[i]
			}
		}
		return result
	},

	// Port of Util::keyed_array($array, 'key')
	// Rows -> key=>row map (alias for associated without value).
	keyed_array: function(array, key) {
		return Util.associated(array, key)
	},

	// Port of Util::in($array)
	// Builds a safe SQL IN clause string from an array of values.
	in: function(array) {
		if (!array || !array.length) return '0'
		// Ensure all values are escaped (numbers are safe, strings get quoted)
		return array.map(function(v) {
			if (typeof v === 'number') return v
			return "'" + String(v).replace(/'/g, "''") + "'"
		}).join(',')
	},

	// Port of Util::sortname($name)
	// Strips articles for sortable form.
	sortname: function(name) {
		if (!name) return ''
		// Match PHP: strip punctuation, lowercase, then remove leading article at word boundary
		name = name.replace(/[-:"'?!]/g, '')
		name = name.toLowerCase()
		name = name.replace(/^(the|a|an)\b\s*/i, '')
		return name.trim()
	},

	// Port of Util::englishify($array)
	// Array -> "A, B and C" string.
	englishify: function(array) {
		if (!array) return ''
		if (!Array.isArray(array)) return String(array)
		if (!array.length) return ''
		if (array.length === 1) return String(array[0])
		if (array.length === 2) return array[0] + ' and ' + array[1]
		return array.slice(0, -1).join(', ') + ' and ' + array[array.length - 1]
	},

	// Strip +/- prefixes from ID arrays, return absolute numeric IDs.
	abs: function(array) {
		if (!Array.isArray(array)) return []
		var result = []
		for (var i = 0; i < array.length; i++) {
			var v = Math.abs(parseInt(array[i], 10))
			if (v > 0) result.push(v)
		}
		return result
	},

	// Port of Util::paged($total, $page, $per_page)
	// Returns pagination metadata.
	paged: function(total, page, perPage) {
		perPage = perPage || 25
		page = page || 1
		var pages = Math.ceil(total / perPage) || 1
		if (page > pages) page = pages
		return {
			total: total,
			page: page,
			pages: pages,
			per_page: perPage,
			from: ((page - 1) * perPage),
			to: Math.min(page * perPage, total)
		}
	},

	// Port of Util::median($array)
	median: function(array) {
		if (!array || !array.length) return 0
		var sorted = array.slice().sort(function(a, b) { return a - b })
		var mid = Math.floor(sorted.length / 2)
		if (sorted.length % 2) return sorted[mid]
		return (sorted[mid - 1] + sorted[mid]) / 2
	},

	// Port of Util::is_json_object($string)
	is_json_object: function(str) {
		if (typeof str !== 'string') return false
		try {
			var parsed = JSON.parse(str)
			return typeof parsed === 'object' && parsed !== null
		} catch (e) {
			return false
		}
	},

	// Port of Util::sort_by_field($array, $field, $direction)
	// NOTE: PHP defaults to descending ($asc=false). JS defaults to ascending for
	// compatibility with filter engine. Insight callers must pass 'desc' explicitly.
	sort_by_field: function(array, field, direction) {
		if (direction === undefined || direction === null) direction = 'asc'
		var mult = (direction === 'desc' || direction === false) ? -1 : 1
		return array.slice().sort(function(a, b) {
			var va = a[field], vb = b[field]
			if (va === vb) return 0
			if (va === null || va === undefined) return 1
			if (vb === null || vb === undefined) return -1
			return (va < vb ? -1 : 1) * mult
		})
	},

	// Mirrors the legacy PHP Stream\Util::sort_by_field implementation exactly.
	// It casts values to float like PHP. We preserve original order on equal
	// values so desc sorts don't invert tie groups during JS array reversal.
	sort_by_field_php: function(array, field, asc) {
		var indexed = array.slice().map(function(item, index) {
			return { item: item, index: index }
		})
		var toFloat = function(value) {
			var parsed = parseFloat(value)
			return Number.isNaN(parsed) ? 0 : parsed
		}
		indexed.sort(function(left, right) {
			var a = toFloat(left.item[field])
			var b = toFloat(right.item[field])
			if (a === b) return left.index - right.index
			return asc ? (a < b ? -1 : 1) : (a > b ? -1 : 1)
		})
		return indexed.map(function(entry) { return entry.item })
	},

	// Port of Util::adjustedRating and Util::adjustedRatingAdvanced
	adjustedRating: function(value, lessthan) {
		var hasDecimals = String(value).indexOf('.')
		if (hasDecimals !== -1) {
			var decimals = String(value).split('.')[1].length
			if (decimals < 2) {
				value = lessthan ? value + 0.04 : value - 0.04
			}
		} else {
			value = lessthan ? value + 0.04 : value - 0.04
		}
		return value
	},

	adjustedRatingAdvanced: function(value, logic, lessthan, band) {
		var hasDecimals = String(value).indexOf('.')
		if (hasDecimals !== -1) {
			var decimals = String(value).split('.')[1].length
			if (decimals < 2) {
				value = lessthan ? value + 0.04 : value - 0.04
			}
		} else {
			if (lessthan) {
				value += band ? 0.94 : 0.04
			} else {
				value -= 0.04
			}
		}
		return value
	},

	// Port of Util::merge($array, $field)
	// Extracts comma-separated values from a field across rows, returns unique flat array.
	merge: function(array, field) {
		var result = []
		for (var i = 0; i < array.length; i++) {
			var val = array[i][field]
			if (val) {
				var parts = String(val).split(',')
				for (var j = 0; j < parts.length; j++) {
					if (parts[j] !== '' && result.indexOf(parts[j]) === -1) {
						result.push(parts[j])
					}
				}
			}
		}
		return result
	},

	// Port of Util::array_flatten($array)
	array_flatten: function(array) {
		var result = []
		for (var i = 0; i < array.length; i++) {
			if (Array.isArray(array[i])) {
				var flat = Util.array_flatten(array[i])
				for (var j = 0; j < flat.length; j++) result.push(flat[j])
			} else {
				result.push(array[i])
			}
		}
		return result
	},

	// Port of Util::array_merge_recursive_distinct
	// Deep-merges objects; arrays are replaced, not concatenated.
	array_merge_recursive_distinct: function(a, b) {
		var result = {}
		var keys = Object.keys(a)
		for (var i = 0; i < keys.length; i++) result[keys[i]] = a[keys[i]]

		var bkeys = Object.keys(b)
		for (var j = 0; j < bkeys.length; j++) {
			var key = bkeys[j]
			if (result[key] !== undefined && typeof result[key] === 'object' && !Array.isArray(result[key]) &&
				typeof b[key] === 'object' && !Array.isArray(b[key]) && b[key] !== null) {
				result[key] = Util.array_merge_recursive_distinct(result[key], b[key])
			} else {
				result[key] = b[key]
			}
		}
		return result
	},

	// Port of PHP date() for unix timestamps
	// Supports: Y (4-digit year), y (2-digit), m (month 01-12), d (day 01-31),
	// c (month 1-12 no pad), w (day of week 0-6 Sun=0), H (hour 00-23)
	date: function(format, timestamp) {
		var d = new Date(timestamp * 1000)
		var map = {
			'Y': d.getFullYear(),
			'y': String(d.getFullYear()).slice(-2),
			'm': ('0' + (d.getMonth() + 1)).slice(-2),
			'c': d.getMonth() + 1,
			'd': ('0' + d.getDate()).slice(-2),
			'w': d.getDay(),
			'H': ('0' + d.getHours()).slice(-2),
			'j': d.getDate()
		}
		return format.replace(/[YymdcwHj]/g, function(ch) {
			return (map[ch] !== undefined) ? map[ch] : ch
		})
	},

	// Port of Util::week_start($timestamp)
	// Returns unix timestamp for the start of the week (Monday) containing the given date.
	// PHP default: $sundayAsStart = true, so week starts on Sunday
	week_start: function(timestamp, sundayAsStart) {
		if (sundayAsStart === undefined) sundayAsStart = true
		var d = new Date(timestamp * 1000)
		d.setUTCHours(0, 0, 0, 0)
		var todayStart = Math.floor(d.getTime() / 1000)
		var w = d.getUTCDay() // 0=Sunday
		if (w > 0) {
			return todayStart - ((w - (sundayAsStart ? 0 : 1)) * 86400)
		}
		return todayStart
	},

	// Port of Util::date_end($timestamp)
	// Returns end of the day (23:59:59) for the given timestamp.
	date_end: function(timestamp) {
		var d = new Date(timestamp * 1000)
		d.setUTCHours(23, 59, 59, 0)
		return Math.floor(d.getTime() / 1000)
	},

	// Port of Util::date_start($timestamp)
	// Returns start of the day (00:00:00) for the given timestamp.
	date_start: function(timestamp) {
		var d = new Date(timestamp * 1000)
		d.setUTCHours(0, 0, 0, 0)
		return Math.floor(d.getTime() / 1000)
	},

	// Port of Util::mktime($hour, $min, $sec, $parts)
	// $parts is [day, month, year]
	mktime: function(hour, min, sec, parts) {
		var d = new Date(Date.UTC(parts[2], parts[1] - 1, parts[0], hour, min, sec))
		return Math.floor(d.getTime() / 1000)
	},

	// Port of Util::weekofyear($timestamp, $sunday_start)
	weekofyear: function(timestamp, sundayStart) {
		var d = new Date(timestamp * 1000)
		d.setUTCHours(0, 0, 0, 0)
		var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
		var dayOfYear = Math.floor((d - yearStart) / 86400000) + 1
		var wday = d.getUTCDay()
		if (!sundayStart) {
			wday = (wday === 0) ? 6 : wday - 1
		}
		return Math.ceil((dayOfYear - wday + (sundayStart ? 7 : 6)) / 7)
	},

	// Port of PHP TIME() — current unix timestamp
	time: function() {
		return Math.floor(Date.now() / 1000)
	},

	// Port of Util::slug($string)
	// Converts a string to a URL-friendly slug.
	slug: function(str) {
		if (!str) return ''
		// Match PHP `Util::slug`: strip everything except letters, digits, and
		// whitespace — including hyphens — then collapse spaces to dashes.
		// (PHP's char-class `[^0-9|^a-zA-Z ]` keeps only alphanumerics +
		// literal `|` + `^` + space.) Preserving hyphens in JS diverges from
		// PHP, so e.g. "sindy-01" would slug to "sindy-01" here vs "sindy01"
		// upstream.
		return String(str)
			.replace(/&/g, ' and ')
			.replace(/[^0-9a-zA-Z ]+/g, '')
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.replace(/^\s+|\s+$/g, '')
			.replace(/ /g, '-')
	},

	// Port of Util::percentile($sorted_array, $value)
	// Returns the percentile rank (0-100) of value within a pre-sorted array.
	percentile: function(sorted, value) {
		if (!sorted || !sorted.length) return 0
		var below = 0
		for (var i = 0; i < sorted.length; i++) {
			if (sorted[i] < value) below++
		}
		return Math.round((below / sorted.length) * 100)
	},

	// Concentration index calculation (port of Util::getHConcentrationIndex)
	getHConcentrationIndex: function(playCounts) {
		var n = playCounts.length
		if (n === 0) return 0
		if (n === 1) return 1

		var total = 0
		for (var i = 0; i < n; i++) total += playCounts[i]
		var mean = total / n
		if (mean === 0) return 0

		var absDevSum = 0
		for (var j = 0; j < n; j++) absDevSum += Math.abs(playCounts[j] - mean)

		var concentration = 1 - (absDevSum / (n * mean))
		return Math.max(0, Math.min(1, Math.round(concentration * 100) / 100))
	},

	// Parse legacy comma-separated or JSON-encoded integer ID lists.
	parse_id_list: function(value) {
		if (!value) return []
		if (Array.isArray(value)) {
			return value.map(function(id) { return parseInt(id, 10) }).filter(function(id) { return !isNaN(id) })
		}
		var raw = String(value).trim()
		if (!raw) return []
		if (raw[0] === '[') {
			try {
				var parsed = JSON.parse(raw)
				if (Array.isArray(parsed)) {
					return parsed.map(function(id) { return parseInt(id, 10) }).filter(function(id) { return !isNaN(id) })
				}
			} catch (e) { /* fall through to CSV parse */ }
		}
		return raw.split(',').map(function(id) { return parseInt(id, 10) }).filter(function(id) { return !isNaN(id) })
	}

}

export default Util
